import { mediaMobileOnly, mediaTablet } from '../../theme/media';
import { style } from 'typestyle';
import { percent, px, important } from 'csx';
import { PillStyleDisabled } from '../../components/pill';
import {
  colorGrey,
  colorGreyAccent,
  colorLightGrey,
  colorCrimson,
  colorBlueGrey,
  colorLightBlueGrey,
  colorDarkBlueGrey,
  colorWhite,
  colorBlack,
  colorBrand,
  colorSlate,
} from '../../theme/color';
import { resetAnchorStyles } from '../../theme/reset';
import { NestedCSSProperties } from 'typestyle/lib/types';
import { shadowElevation2, shadowElevation3 } from '../../theme/shadow';
import { defaultLink, mixinTextEllipsis } from '../../theme/mixin';
import { fontDefault } from '../../theme/font';

export const borderStyle = `${px(1)} solid ${colorLightGrey.toString()}`;

const messageStyle: NestedCSSProperties = {
  marginTop: px(50),
  marginBottom: px(50),
};

const errorMessageStyle: NestedCSSProperties = {
  color: colorCrimson.toString(),
  display: 'block',
  fontSize: px(12),
  marginTop: px(16),
};

export const styles = {
  container: style(
    {
      maxWidth: px(855),
      width: percent(100),
      margin: 'auto',
      display: 'flex',
      background: 'white',
    },
    mediaTablet({
      display: 'grid',
      gridTemplate: '117px auto / 63% 37%',
      $nest: {
        '&.with-user': {
          gridTemplate: '138px auto / 59% 41%',
        },
      },
    }),
    mediaMobileOnly({
      flexDirection: 'column',
      width: percent(100),
      paddingTop: px(57),
    }),
  ),
  loading: style(messageStyle),
  empty: style(messageStyle, {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  checkoutCard: style({
    background: colorWhite.toString(),
    border: borderStyle,
    width: percent(100),
    borderRadius: px(10),
    boxShadow: `0 ${px(2)} ${px(4)} ${px(1)} rgb(0, 0, 0, 0.3)`,
    margin: `0 0 ${px(16)}`,
    overflow: 'hidden',
    position: 'relative',
    $nest: {
      '> div': {
        display: 'flex',
        alignItems: 'center',
      },
      '> div:first-child ~ div': {
        borderTop: `${px(1)} solid ${colorGreyAccent.toString()}`,
        marginBottom: px(4),
        justifyContent: 'space-between',
      },
      img: {
        width: px(80),
        height: 'auto',
      },
    },
  }),
  checkoutCardRemove: style({
    display: 'flex',
    alignItems: 'center',
    margin: `${px(12)} ${px(16)}`,
    fontSize: px(12),
    $nest: {
      span: {
        marginLeft: px(8),
      },
    },
  }),
  checkoutCardPlay: style({
    position: 'absolute',
    left: px(40),
    top: px(40),
    $nest: {
      svg: {
        marginLeft: px(4),
      },
      '&.playing svg': {
        marginLeft: 0,
      },
    },
  }),
  checkoutCardViewLicense: style(shadowElevation2, {
    margin: `0 0 ${px(4)} ${px(16)}`,
  }),
  checkoutCardArtwork: style({
    flexShrink: 0,
  }),
  checkoutCardArtist: style({
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    margin: `0 16px`,
    $nest: {
      p: {
        margin: 0,
        fontSize: px(12),
        ...mixinTextEllipsis,
      },
      '& a': {
        $nest: {
          '&, &:visited, &:active': {
            color: colorBlack.toHexString(),
            textDecoration: 'none',
          },
        },
      },
      '& p:first-child': {
        fontWeight: 600,
      },
    },
  }),
  checkoutCardButtonBar: style({
    display: 'flex',
    flexDirection: 'row',
    margin: `0 ${px(12)} ${px(8)} auto`,
    $nest: {
      '*': {
        margin: px(4),
      },
    },
  }),
  checkoutCardButtonWithBadge: style(shadowElevation2, {
    position: 'relative',
    $nest: {
      '& > .badge': {
        position: 'absolute',
        background: colorBrand.toHexString(),
        color: colorWhite.toString(),
        top: px(-8),
        right: px(-10),
      },
    },
  }),
  checkoutCardArtistTotal: style({
    textAlign: 'right',
    marginRight: px(16),
  }),
  checkoutVideoPrivacyDetails: style({
    fontSize: px(12),
    padding: '0 15px 17px',
    $nest: {
      a: {
        display: 'block',
        textAlign: 'center',
        ...defaultLink,
      },
    },
  }),
  checkoutSummaryCouponForm: style({
    display: 'flex',
    fontSize: px(12),
    lineHeight: px(16),
    alignItems: 'flex-end',
    $nest: {
      label: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: percent(60),
      },
      input: {
        background: colorLightGrey.toString(),
        border: borderStyle,
        boxShadow: `${px(1)} ${px(1)} ${colorDarkBlueGrey.toString()} inset`,
        padding: `${px(4)} ${px(8)}`,
        fontWeight: 'normal',
        fontFamily: fontDefault,
        marginTop: px(4),
        width: percent(100),
        $nest: {
          '&[disabled]': {
            color: colorSlate.toHexString(),
          },
        },
      },
      button: {
        width: percent(35),
        marginLeft: 'auto',
      },
    },
  }),
  checkoutSubtotal: style({
    padding: `${px(16)} ${px(32)}`,
    display: 'flex',
    alignItems: 'flex-end',
    flexDirection: 'column',
    $nest: {
      span: {
        marginBottom: px(4),
      },
    },
  }),
  checkoutCardListContainer: style(
    mediaTablet({
      gridArea: '1/1/3/2',
    }),
    mediaMobileOnly({
      width: percent(100),
    }),
  ),
  checkoutCardList: style(
    {
      background: colorGrey.toString(),
      border: borderStyle,
      padding: `${px(16)} ${px(16)} 0`,
      $nest: {
        h5: {
          fontWeight: 'bold',
          fontSize: px(16),
          lineHeight: px(16),
          margin: `0 0 ${px(8)}`,
          textAlign: 'center',
        },
      },
    },
    mediaTablet({
      maxHeight: px(450),
      overflowY: 'scroll',
    }),
  ),
  userInfo: style(
    mediaTablet({
      gridArea: '1/2/2/3',
      border: borderStyle,
      borderBottom: 0,
      borderTop: 0,
    }),
  ),
  checkoutSummary: style(
    {
      borderTop: borderStyle,
      padding: px(0),
      $nest: {
        '& a': {
          color: colorBrand.toHexString(),
        },
      },
    },
    mediaTablet({
      border: borderStyle,
      gridArea: '2/2/3/3',
      $nest: {
        '.with-user &': {
          borderTop: 'none',
        },
      },
    }),
    mediaMobileOnly({
      width: percent(100),
    }),
  ),
  checkoutSummaryItem: style(
    {
      padding: px(16),
      borderBottom: borderStyle,
      textAlign: 'center',
      position: 'relative',
      zIndex: 11,
      background: colorWhite.toHexString(),
      $nest: {
        '& .totalText': {
          fontSize: px(16),
        },
        '& .checkoutButton': {
          width: 'calc(100% - 32px)',
          margin: '8px 16px 0',
        },
        '&:nth-child(4)': {
          padding: px(8),
        },
        '& p:first-child': {
          margin: `${px(16)} ${px(0)} ${px(4)}`,
          fontWeight: 'bold',
          lineHeight: px(16),
          fontSize: px(12),
        },
        '& input + label': {
          textAlign: 'left',
          paddingLeft: px(35),
        },
        '& input + label:before': {
          border: borderStyle,
          content: '',
          height: px(24),
          left: 0,
          background: colorLightGrey.toString(),
          marginTop: px(-15),
          position: 'absolute',
          textAlign: 'center',
          top: percent(50),
          transition: `all 0.125s ease-in-out`,
          width: px(24),
          boxShadow: `${px(1)} ${px(1)} ${colorDarkBlueGrey.toString()} inset`,
        },
      },
    },
    mediaTablet({
      zIndex: 0,
    }),
  ),
  checkoutCheckbox: style({
    $nest: {
      div: {
        margin: px(0),
      },
    },
  }),
  stickyHeader: style(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'fixed',
      backgroundColor: colorLightBlueGrey.toString(),
      padding: `${px(12)} ${px(16)}`,
      fontSize: px(12),
      zIndex: 10,
      top: px(60),
      width: percent(100),
      borderBottom: borderStyle,
      $nest: {
        '& .totalText': {
          fontSize: px(16),
          $nest: {
            '& :first-child': {
              fontSize: px(12),
            },
            '&, & > *': {
              marginBottom: 0,
            },
          },
        },
      },
    },
    shadowElevation3,
    mediaTablet({
      display: 'none',
    }),
  ),
  stickyHeaderBtn: style({
    marginLeft: 'auto',
    $nest: {
      '&:disabled': {
        ...PillStyleDisabled,
      },
    },
  }),
  stickyHeaderLabel: style({
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
    $nest: {
      'span:last-of-type': {
        fontSize: px(16),
        fontWeight: 600,
      },
    },
  }),
  loginBtn: style({
    width: percent(50),
  }),
  totalText: style({
    display: 'block',
    lineHeight: px(16),
    marginBottom: px(8),
    $nest: {
      '&:last-of-type': {
        fontWeight: 600,
      },
    },
  }),
  tooltip: style({
    $nest: {
      '&.type-dark.place-left': {
        // Need these for high enough specificity, but it couples it to the libraries behaviour
        maxWidth: px(150),
        padding: '4px 8px',
        backgroundColor: colorBrand.toHexString(),
        borderRadius: px(4),
        $nest: {
          '&::after': {
            borderLeftColor: colorBrand.toHexString(),
          },
        },
      },
    },
  }),
  smallText: style({
    fontSize: px(12),
    margin: `0 0 ${px(4)}`,
  }),
  stripeLogo: style({
    margin: `${px(16)} auto ${px(0)}`,
    width: percent(100),
    display: 'flex',
    justifyContent: 'center',
  }),
  couponMessage: style({
    margin: important(`${px(0)} ${px(0)} ${px(8)}`),
    display: 'flex',
    width: percent(100),
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    $nest: {
      img: {
        height: px(14),
        width: px(14),
        marginRight: px(4),
      },
      span: {
        color: colorBlueGrey.toString(),
        marginLeft: px(4),
      },
    },
  }),
  errorMessage: style(errorMessageStyle),
  errorMessageLeft: style({
    ...errorMessageStyle,
    textAlign: 'left',
  }),
};
